import type { SearchResults } from '#shared/types/search'
import type { UserProfile } from '#shared/types/User'
import { useTypesense } from '@/composables/typesense/useTypesense'

type VolunteerSearchHit = UserProfile & {
    objectID?: string
    profileImage?: string
}

type SearchOptions = {
    page?: number
    hitsPerPage?: number
    filters?: string
    sort_by?: string
    ruleContexts?: string[]
    attributesToRetrieve?: string[]
    requireProfileImg?: boolean
    profileImgScanPages?: number
}

/**
 * Composable for volunteers-related functionality
 */
export function useVolunteers() {
    const { keywordSearch, loadFacets } = useTypesense()
    const FEATURED_VOLUNTEERS_COUNT = 4
    const DEFAULT_PER_PAGE = 24
    const PROFILE_IMG_SCAN_PAGES = 5

    function hasProfileImg(volunteer: VolunteerSearchHit): boolean {
        const profileImg = volunteer.profileImg || volunteer.profileImage
        return typeof profileImg === 'string' && profileImg.trim().length > 0
    }

    function addVolunteersWithProfileImg(
        target: VolunteerSearchHit[],
        volunteers: VolunteerSearchHit[],
        seenIds: Set<string>,
        limit: number
    ) {
        for (const volunteer of volunteers) {
            const key = volunteer.id || volunteer.objectID
            if (!hasProfileImg(volunteer) || (key && seenIds.has(key))) continue

            if (key) seenIds.add(key)
            target.push(volunteer)

            if (target.length >= limit) {
                return
            }
        }
    }

    async function getFeaturedVolunteers(
        featuredCount: number = FEATURED_VOLUNTEERS_COUNT,
        filters?: string
    ): Promise<UserProfile[]> {
        try {
            const results = await searchVolunteers('', {
                filters,
                hitsPerPage: featuredCount,
                requireProfileImg: true,
                profileImgScanPages: PROFILE_IMG_SCAN_PAGES
            })
            return results.hits || []
        } catch (error) {
            console.error('[useVolunteers] getFeaturedVolunteers error:', error)
            return []
        }
    }

    async function getShowMoreVolunteersCount(
        featuredCount: number = FEATURED_VOLUNTEERS_COUNT,
        filters?: string
    ): Promise<number> {
        // Always filter for published visibility
        const visibilityFilter = 'visibility:=`published`'
        const combinedFilters = filters ? `${visibilityFilter} && ${filters}` : visibilityFilter
        // Only the total is needed: fetch a single id instead of 50 full profiles.
        const allVolunteers = await keywordSearch('volunteers', '', {
            filters: combinedFilters,
            hitsPerPage: 1,
            includeFields: 'id'
        })
        const countNextVolunteer = allVolunteers.nbHits - featuredCount
        return countNextVolunteer > 0 ? countNextVolunteer : 0
    }

    /**
     * Search volunteers using hybrid search (semantic + keyword)
     * Combines embedding field with text fields for better relevance
     */
    const searchVolunteers = async (query: string = '', options: SearchOptions = {}): Promise<SearchResults> => {
        // Always filter for published visibility
        const visibilityFilter = 'visibility:=`published`'
        const combinedFilters = options.filters ? `${visibilityFilter} && ${options.filters}` : visibilityFilter
        const page = options.page || 0
        const hitsPerPage = options.hitsPerPage || DEFAULT_PER_PAGE
        const searchOptions = {
            filters: combinedFilters,
            hitsPerPage,
            page,
            sort_by: options.sort_by,
            // Hybrid search: combine embedding with text fields
            queryBy: 'embedding,firstName,lastName,interests'
        }

        const results = await keywordSearch<VolunteerSearchHit>('volunteers', query, searchOptions)

        if (!options.requireProfileImg || page > 0) {
            return results
        }

        const hitsWithProfileImg: VolunteerSearchHit[] = []
        const seenIds = new Set<string>()
        addVolunteersWithProfileImg(hitsWithProfileImg, results.hits || [], seenIds, hitsPerPage)

        const maxScanPages = options.profileImgScanPages || PROFILE_IMG_SCAN_PAGES
        let nextPage = page + 1

        while (
            hitsWithProfileImg.length < hitsPerPage &&
            nextPage < (results.nbPages || 0) &&
            nextPage < maxScanPages
        ) {
            const nextResults = await keywordSearch<VolunteerSearchHit>('volunteers', query, {
                ...searchOptions,
                page: nextPage
            })
            addVolunteersWithProfileImg(hitsWithProfileImg, nextResults.hits || [], seenIds, hitsPerPage)
            nextPage += 1
        }

        return {
            ...results,
            hits: hitsWithProfileImg.slice(0, hitsPerPage)
        }
    }

    async function loadVolunteerFacets(
        options: {
            facets?: string[]
            filters?: string
        } = {}
    ): Promise<any> {
        // Always filter for published visibility
        const visibilityFilter = 'visibility:=`published`'
        const combinedFilters = options.filters ? `${visibilityFilter} && ${options.filters}` : visibilityFilter
        return await loadFacets('volunteers', {
            facets: options.facets,
            filterBy: combinedFilters
        })
    }

    return {
        getFeaturedVolunteers,
        getShowMoreVolunteersCount,
        searchVolunteers,
        loadVolunteerFacets
    }
}
